/**
 * Custom Image wrapper
 *
 * Adds:
 * - Preloading support (high‑res or low‑res)
 * - Auto aspect‑ratio scaling
 * - Pixel‑density scaling
 * - Blur placeholder for non‑preloaded images
 *
 * Compatible with:
 * - Node 20–24
 * - Next.js 13/14 (next/future/image)
 */

import usePreloadImage from "components/_hooks/use-preload-image";
import type { ImageProps as NextImageProps } from "next/dist/client/future/image";
import NextImage from "next/future/image";
import { FC, useEffect } from "react";
import type { SetRequired } from "type-fest";

/**
 * Extended Image Props
 *
 * We require width + height but allow:
 * - aspectRatio: override calculated ratio
 * - maxWidth / maxHeight: auto-scale the rendered image
 * - pixelDensity: multiply size (for 2x retina assets)
 * - preload: high‑res preload or low‑res fallback preload
 * - src: supports string | StaticImageData | object
 */
export type ImageProps = SetRequired<
  Omit<NextImageProps, "src">,
  "width" | "height"
> & {
  aspectRatio?: number;
  maxHeight?: number;
  maxWidth?: number;
  pixelDensity?: number;
  preload?: boolean;
  src?: NextImageProps["src"];
};

export const Image: FC<ImageProps> = ({
  pixelDensity = 1,
  preload,
  ...props
}) => {
  const preloadImage = usePreloadImage();

  const {
    src,
    width,
    height,
    maxWidth,
    maxHeight,
    aspectRatio,
    ...rest
  } = props;

  /** Natural aspect ratio (or override) */
  const aspect = aspectRatio ?? +width / +height;

  /**
   * Preload logic:
   * - preload=true → load high‑res first
   * - preload=false → load tiny 32px blurred version
   */
  useEffect(() => {
    if (!src || typeof src !== "string") return;

    const targetWidth = maxWidth
      ? maxWidth
      : maxHeight
        ? maxHeight * aspect
        : +width;

    if (preload) {
      preloadImage({
        src,
        quality: props.quality ? +props.quality : 75,
        width: targetWidth,
      });
    } else {
      preloadImage({
        src,
        quality: 1,
        width: 32,
      });
    }
  }, [
    src,
    preload,
    preloadImage,
    width,
    maxWidth,
    maxHeight,
    aspect,
    props.quality,
  ]);

  /** Skip invalid/uninitialized images */
  if (!src || src === "undefined") return null;

  /** Sanitize string URLs to force https (your original logic) */
  const sanitizedSrc =
    typeof src === "string"
      ? src.replace(/^(http:)?\/\//, "https://")
      : src;

  /**
   * Final rendered width/height with pixel‑density scaling
   */
  const finalWidth = Math.round(
    +(maxWidth ? maxWidth : maxHeight ? maxHeight * aspect : width) *
    pixelDensity
  );

  const finalHeight = Math.round(
    +(maxHeight ? maxHeight : maxWidth ? maxWidth / aspect : height) *
    pixelDensity
  );

  return (
    <NextImage
      {...rest}
      /** preload prioritizes this image */
      priority={(typeof src !== "string" && preload) || rest.priority}
      /** Blur placeholder for non-preloaded images */
      placeholder={!preload ? "blur" : undefined}
      blurDataURL={
        !preload && typeof sanitizedSrc === "string"
          ? `/_next/image?url=${encodeURIComponent(sanitizedSrc)}&w=32&q=1`
          : undefined
      }
      src={sanitizedSrc}
      width={finalWidth}
      height={finalHeight}
    />
  );
};
